/** Environments: branch guardrails, defaults and the write/patch/read schemas (SPEC §4, §16.1). */
import { z } from 'zod';
import { EnvKind, slug, uuid } from './common.js';
import { BranchPatterns } from './git.js';
import { matchesEveryBranch } from './glob.js';

/** The guardrail fields of an environment, as stored and as `EnvironmentWrite` / `EnvironmentPatch` carry them. */
export interface EnvironmentGuardrail {
  isProtected: boolean;
  allowedBranchPatterns: readonly string[];
  untrustedRefsBlocked: boolean;
}

/**
 * What a protected environment must hold (SPEC §16.1): the branches it accepts (at least one pattern, and none that
 * matches every branch) and untrusted refs blocked (forks and external pull requests never reach it). Checked when an
 * environment is created and by every patch that touches one of these fields; an unprotected environment may list
 * patterns (they are enforced) or none (any branch).
 */
export function environmentGuardrailIssues(e: EnvironmentGuardrail): { path: string[]; message: string }[] {
  if (!e.isProtected) return [];
  const issues: { path: string[]; message: string }[] = [];
  const patterns = Array.isArray(e.allowedBranchPatterns) ? e.allowedBranchPatterns : [];
  if (patterns.length === 0) issues.push({ path: ['allowedBranchPatterns'], message: 'a protected environment needs at least one allowed branch pattern' });
  else if (patterns.some(matchesEveryBranch)) issues.push({ path: ['allowedBranchPatterns'], message: 'a protected environment cannot allow every branch (*)' });
  if (e.untrustedRefsBlocked === false) issues.push({ path: ['untrustedRefsBlocked'], message: 'a protected environment always blocks untrusted refs (forks, external pull requests)' });
  return issues;
}

/** The branch the protected default environments accept unless the project names another (`productionBranch`). */
export const DEFAULT_PRODUCTION_BRANCH = 'master';
/** Color of a new environment that does not name one (`color: null` stores none). */
export const DEFAULT_ENVIRONMENT_COLORS: Record<EnvKind, string> = {
  development: '#30A46C', preview: '#0090FF', staging: '#F5A524', preprod: '#8E4EC6', production: '#E5484D',
};

export interface EnvironmentPreset extends EnvironmentGuardrail {
  slug: string;
  name: string;
  kind: EnvKind;
  sortOrder: number;
  color: string;
  allowedBranchPatterns: string[];
}

/**
 * What `createDefaultEnvironments` creates (SPEC §4): staging (any branch) → preprod (the production branch and
 * `release/*`) → production (the production branch), the last two protected, untrusted refs blocked everywhere.
 */
export function defaultEnvironmentPresets(productionBranch: string = DEFAULT_PRODUCTION_BRANCH): EnvironmentPreset[] {
  return [
    { slug: 'staging', name: 'Staging', kind: 'staging', sortOrder: 10, color: DEFAULT_ENVIRONMENT_COLORS.staging, isProtected: false, allowedBranchPatterns: [], untrustedRefsBlocked: true },
    { slug: 'preprod', name: 'Pre-production', kind: 'preprod', sortOrder: 20, color: DEFAULT_ENVIRONMENT_COLORS.preprod, isProtected: true, allowedBranchPatterns: [productionBranch, 'release/*'], untrustedRefsBlocked: true },
    { slug: 'production', name: 'Production', kind: 'production', sortOrder: 30, color: DEFAULT_ENVIRONMENT_COLORS.production, isProtected: true, allowedBranchPatterns: [productionBranch], untrustedRefsBlocked: true },
  ];
}

export const ENVIRONMENT_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const environmentColor = z.string().regex(ENVIRONMENT_COLOR_PATTERN, 'a hex color such as #E5484D');
const environmentName = z.string().trim().min(1).max(120);
/** Promotion order, lower first (the defaults use 10, 20, 30). */
const environmentSortOrder = z.number().int().min(0).max(100_000);

/**
 * `POST /projects/{projectId}/environments` (api/openapi.yaml `EnvironmentWrite`). Strict, so a misspelled guardrail
 * (`protected` for `isProtected`) is refused instead of silently creating an unprotected environment. `color` absent →
 * the kind's default; `sortOrder` absent → after the last environment.
 */
export const EnvironmentWrite = z.object({
  slug,
  name: environmentName,
  kind: EnvKind,
  sortOrder: environmentSortOrder.optional(),
  color: environmentColor.nullable().optional(),
  isProtected: z.boolean().default(false),
  allowedBranchPatterns: BranchPatterns.default([]),
  untrustedRefsBlocked: z.boolean().default(true),
}).strict().superRefine((e, ctx) => {
  for (const issue of environmentGuardrailIssues(e)) ctx.addIssue({ code: 'custom', ...issue });
});
export type EnvironmentWrite = z.infer<typeof EnvironmentWrite>;

/**
 * `PATCH /environments/{environmentId}` (merge patch). `slug` may change only while nothing uses it yet (no build,
 * shadow run, env requirement or env assertion names it); `kind` never changes. `archived: true` is refused while
 * the environment has active builds, env vars or profile bindings. The guardrail rules apply to the patched result.
 */
export const EnvironmentPatch = z.object({
  slug: slug.optional(),
  name: environmentName.optional(),
  sortOrder: environmentSortOrder.optional(),
  color: environmentColor.nullable().optional(),
  isProtected: z.boolean().optional(),
  allowedBranchPatterns: BranchPatterns.optional(),
  untrustedRefsBlocked: z.boolean().optional(),
  archived: z.boolean().optional(),
}).strict();
export type EnvironmentPatch = z.infer<typeof EnvironmentPatch>;

export const Environment = z.object({
  id: uuid, projectId: uuid, slug: z.string(), name: z.string(), kind: EnvKind, sortOrder: z.number().int(),
  color: z.string().nullable(), isProtected: z.boolean(), allowedBranchPatterns: z.array(z.string()),
  untrustedRefsBlocked: z.boolean(), createdAt: z.string(), archivedAt: z.string().nullable(),
  /** The project's version (environments are embedded in it): send it as `If-Match` on `PATCH /environments/{id}`. */
  etag: z.string().optional(),
});
export type Environment = z.infer<typeof Environment>;
